import Phaser from 'phaser';
import { PLAYER_FX } from '../config/GameConfig';

interface GameOverData {
  score: number;
  highScore: number;
  kills: number;
  wave: number;
  coins: number;
  canRevive: boolean;
  /** 本局是否破纪录（从 MainScene 传入，避免这里再读 storage） */
  beatHighScore: boolean;
  /** beatHighScore=true 时是 +N 超出量；false 时是距纪录还差 N */
  gapToHigh: number;
}

interface PauseData {
  score: number;
  highScore: number;
  kills: number;
  wave: number;
}

interface VictoryData {
  score: number;
  highScore: number;
  kills: number;
  wave: number;
  coins: number;
  /** 累计通关次数（含本次） */
  totalClears: number;
}

interface PauseHandlers {
  onResume: () => void;
  onRestart: () => void;
  onMenu: () => void;
}

interface GameOverHandlers {
  onRevive: () => void;
  onRestart: () => void;
  onMenu: () => void;
}

interface VictoryHandlers {
  /** 切到无尽延伸（保留当前 buff） */
  onContinue: () => void;
  onRestart: () => void;
  onMenu: () => void;
}

const BTN_BASE_CSS = `
  display:block; width:100%; margin:8px 0; padding:11px;
  border-radius:12px; border:1px solid rgba(255,255,255,.25);
  background: linear-gradient(180deg, rgba(255,255,255,.09), rgba(255,255,255,.035)); color:#fff;
  font-size:14px; font-weight:600; cursor:pointer;
  box-shadow: 0 6px 18px rgba(0,0,0,.18);
`;

/**
 * PanelManager —— 暂停 / GameOver DOM 面板
 *
 * 升级面板由 UpgradeManager 自管，所以这里不接管。这个类只负责：
 *   - 暂停面板（继续/重开/返回菜单）
 *   - GameOver 面板（复活[条件]/重开/返回菜单）
 *   - 提供 closeAll() 给 scene shutdown 时清残留
 */
export default class PanelManager {
  private _pauseEl: HTMLElement | null = null;
  private _gameOverEl: HTMLElement | null = null;
  private _victoryEl: HTMLElement | null = null;

  constructor(public scene: Phaser.Scene) {}

  isAnyOpen(): boolean {
    return !!(this._pauseEl || this._gameOverEl || this._victoryEl);
  }

  closeAll(): void {
    this._removeEl('_pauseEl');
    this._removeEl('_gameOverEl');
    this._removeEl('_victoryEl');
  }

  // ─── 暂停 ───────────────────────────────────────────────────────

  showPause(data: PauseData, handlers: PauseHandlers): void {
    if (this._pauseEl) return;
    const overlay = this._buildOverlay();
    const inner = this._buildInner('rgba(76,210,255,.25)');
    inner.innerHTML = `
      <div style="font-size:22px; font-weight:700; color:#4cd2ff; margin-bottom:14px;">
        游戏暂停
      </div>
      <div style="font-size:13px; opacity:.85; line-height:1.7; margin-bottom:14px;">
        当前得分 ${data.score}<br/>
        历史最高 ${Math.max(data.highScore, data.score)}<br/>
        击杀 ${data.kills} · 波次 ${data.wave}
      </div>
    `;

    inner.appendChild(this._buildBtn('继续游戏', { borderColor: '#4cd2ff', color: '#4cd2ff' }, () => {
      this._removeEl('_pauseEl');
      handlers.onResume();
    }));
    inner.appendChild(this._buildBtn('重新开始', {}, () => {
      this._removeEl('_pauseEl');
      handlers.onRestart();
    }));
    inner.appendChild(this._buildBtn('返回主菜单', {}, () => {
      this._removeEl('_pauseEl');
      handlers.onMenu();
    }));

    overlay.appendChild(inner);
    document.body.appendChild(overlay);
    this._pauseEl = overlay;
  }

  // ─── GameOver ──────────────────────────────────────────────────

  showGameOver(data: GameOverData, handlers: GameOverHandlers): void {
    if (this._gameOverEl) return;
    const overlay = this._buildOverlay();
    const inner = this._buildInner('rgba(255,90,90,.35)', 'linear-gradient(180deg,#2a1010 0%,#160808 100%)', '#ffe6e6');

    // 顶部：破纪录金色条 / 距纪录差 N 灰色条 —— 让玩家看到"再玩一局的距离"
    const highlightHtml = data.beatHighScore
      ? `<div style="font-size:13px; color:#ffd166; font-weight:600; margin-bottom:10px;
                     padding:6px 10px; border-radius:8px;
                     background:rgba(255,209,102,.12); border:1px solid rgba(255,209,102,.55);">
           ★ 突破最高分 +${data.gapToHigh}
         </div>`
      : (data.gapToHigh > 0 && data.score > 0
          ? `<div style="font-size:12px; color:#aac4d8; margin-bottom:10px;">
               距最高分还差 <b style="color:#ffe066;">${data.gapToHigh}</b>
             </div>`
          : '');
    inner.innerHTML = `
      <div style="font-size:24px; font-weight:700; color:#ff8888; margin-bottom:8px;">游戏结束</div>
      ${highlightHtml}
      <div style="font-size:14px; opacity:.85; line-height:1.7; margin-bottom:14px;">
        得分 ${data.score}<br/>
        历史最高 ${data.highScore}<br/>
        击杀 ${data.kills}<br/>
        波次 ${data.wave}<br/>
        金币 ${data.coins}
      </div>
    `;

    if (data.canRevive) {
      inner.appendChild(this._buildBtn(
        `花费 ${PLAYER_FX.reviveCoinCost} 金币复活（满血 + 1 层护盾）`,
        { borderColor: 'rgba(255,224,102,.6)', color: '#ffe066' },
        () => {
          this._removeEl('_gameOverEl');
          handlers.onRevive();
        },
      ));
    }
    inner.appendChild(this._buildBtn('重新开始', {}, () => {
      this._removeEl('_gameOverEl');
      handlers.onRestart();
    }));
    inner.appendChild(this._buildBtn('返回主菜单', {}, () => {
      this._removeEl('_gameOverEl');
      handlers.onMenu();
    }));

    overlay.appendChild(inner);
    document.body.appendChild(overlay);
    this._gameOverEl = overlay;
  }

  // ─── Victory（关卡模式通关） ──────────────────────────────────

  showVictory(data: VictoryData, handlers: VictoryHandlers): void {
    if (this._victoryEl) return;
    const overlay = this._buildOverlay();
    const inner = this._buildInner(
      'rgba(255,224,102,.55)',
      'linear-gradient(180deg,#3a2a08 0%,#1a1004 100%)',
      '#fff7d6',
    );

    inner.innerHTML = `
      <div style="font-size:28px; font-weight:800; color:#ffd166; letter-spacing:2px; margin-bottom:6px;">
        ★ 通关达成 ★
      </div>
      <div style="font-size:13px; color:#ffe066; opacity:.85; margin-bottom:14px;">
        本次为第 ${data.totalClears} 次通关
      </div>
      <div style="font-size:14px; opacity:.9; line-height:1.7; margin-bottom:14px;">
        得分 ${data.score}<br/>
        最高 ${data.highScore}<br/>
        击杀 ${data.kills} · 波次 ${data.wave}<br/>
        金币 ${data.coins}
      </div>
    `;

    inner.appendChild(this._buildBtn(
      '继续无尽模式（保留 buff）',
      { borderColor: '#4cd2ff', color: '#4cd2ff' },
      () => {
        this._removeEl('_victoryEl');
        handlers.onContinue();
      },
    ));
    inner.appendChild(this._buildBtn('重新开始', {}, () => {
      this._removeEl('_victoryEl');
      handlers.onRestart();
    }));
    inner.appendChild(this._buildBtn('返回主菜单', {}, () => {
      this._removeEl('_victoryEl');
      handlers.onMenu();
    }));

    overlay.appendChild(inner);
    document.body.appendChild(overlay);
    this._victoryEl = overlay;
  }

  // ─── 私有：DOM helper ─────────────────────────────────────────

  private _buildOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed; inset: 0;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0,0,0,.72);
      backdrop-filter: blur(3px);
      z-index: 9999;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    `;
    return overlay;
  }

  private _buildInner(
    accentBorderRgba: string,
    bg = 'linear-gradient(180deg,#1a2236 0%,#0e1424 100%)',
    color = '#e8f1ff',
  ): HTMLElement {
    const inner = document.createElement('div');
    inner.style.cssText = `
      width: min(84vw, 340px);
      max-height: calc(100vh - 24px);
      overflow-y: auto;
      padding: 24px 20px;
      border-radius: 18px;
      background: ${bg};
      box-shadow:
        0 18px 60px rgba(0,0,0,.65),
        0 0 0 1px ${accentBorderRgba} inset,
        0 0 36px ${accentBorderRgba};
      color: ${color}; text-align: center;
      transform: translateZ(0);
    `;
    return inner;
  }

  private _buildBtn(
    label: string,
    overrides: { borderColor?: string; color?: string },
    onClick: () => void,
  ): HTMLButtonElement {
    const b = document.createElement('button');
    let css = BTN_BASE_CSS;
    if (overrides.borderColor) css += `;border-color:${overrides.borderColor};`;
    if (overrides.color) css += `;color:${overrides.color};`;
    b.style.cssText = css;
    b.textContent = label;
    // 触摸端 touch-action:none 时，手指稍有位移浏览器就会吞掉合成的 click，
    // 导致"点了没反应、再点一次才行"。改用 pointerup 直接触发，click 作为
    // 鼠标/键盘的兜底；用 fired 标志防止两者重复触发同一次操作。
    let fired = false;
    const fire = (e: Event) => {
      e.preventDefault();
      if (fired) return;
      fired = true;
      onClick();
    };
    b.addEventListener('pointerup', fire);
    b.addEventListener('click', fire);
    return b;
  }

  private _removeEl(key: '_pauseEl' | '_gameOverEl' | '_victoryEl'): void {
    const el = this[key];
    if (el?.parentNode) el.parentNode.removeChild(el);
    this[key] = null;
  }
}
